<script setup lang="ts">
import { computed, ref } from "vue";

import { isApiError } from "@/services/Api";

import { useCaseStore, useMessageStore } from "@/stores";

import MigrationDecisions from "./MigrationDecisions.vue";
import MigrationFileSelector from "./MigrationFileSelector.vue";
import MigrationExistingPlans from "./MigrationExistingPlans.vue";
import MigrationDestinationCase from "./MigrationDestinationCase.vue";

import type { DecisionAnswer, MigrationAction, MigrationPlan } from "@/models/RecordMigration";

const emit = defineEmits<{ resetComponent: [] }>();

const DEFAULT_PLAN_ERROR = "Unable to create a migration plan for the selected files. Please try again.";
const DEFAULT_MIGRATE_ERROR = "Unable to migrate the selected files. Please try again.";

type MigrationState =
  | { status: "idle" }
  | { status: "planning" }
  | { status: "requiresInput"; plan: MigrationPlan }
  | { status: "ready"; plan: MigrationPlan; token: string }
  | { status: "migrating"; plan: MigrationPlan; token: string }
  | { status: "error"; message: string };

const caseStore = useCaseStore();

const action = ref<{ action: MigrationAction; answers: DecisionAnswer[]; state: MigrationState }>();
const destinationCaseReference = ref("");
const selectedFileReferences = ref<string[]>([]);
const viewedExistingPlan = ref<MigrationPlan>();

const isBusy = computed(
  () => action.value != null && (action.value.state.status === "planning" || action.value.state.status === "migrating"),
);

const sourceCaseReference = computed(() => caseStore.selectedCaseName!);

const canCreatePlan = computed(
  () =>
    action.value != null &&
    destinationCaseReference.value !== "" &&
    destinationCaseReference.value.toLowerCase() !== sourceCaseReference.value.toLowerCase() &&
    selectedFileReferences.value.length > 0,
);

async function createPlan() {
  if (!canCreatePlan.value || action.value == null) return;

  try {
    action.value.state = { status: "planning" };

    const response = await caseStore.planRecordMigration({
      action: action.value.action,
      answers: action.value.answers,
      fileReferences: selectedFileReferences.value,
      sourceCaseReference: sourceCaseReference.value,
      destinationCaseReference: destinationCaseReference.value,
    });

    action.value.state =
      response.plan.status === "ready" && response.migrationToken != null
        ? { status: "ready", plan: response.plan, token: response.migrationToken }
        : { status: "requiresInput", plan: response.plan };
  } catch (error) {
    action.value.state = { status: "error", message: getErrorMessage(error, DEFAULT_PLAN_ERROR) };
  }
}

async function confirmMigration() {
  if (action.value?.state.status !== "ready") return;

  const { plan, token } = action.value.state;

  try {
    action.value.state = { status: "migrating", plan, token };

    await caseStore.migrateCaseRecords(plan, token);

    useMessageStore().updateMessage({
      type: "success",
      autoHide: true,
      showReload: false,
      title: "Record Migration",
      message: "The migration has been queued and will be processed shortly.",
    });

    emit("resetComponent");
  } catch (error) {
    const message = getErrorMessage(error, DEFAULT_MIGRATE_ERROR);

    action.value.state = { status: "error", message };

    useMessageStore().updateMessage({
      type: "error",
      autoHide: true,
      showReload: false,
      title: "Record Migration",
      message,
    });
  }
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (isApiError<{ reason?: string; message?: string }>(error)) {
    return error.data?.reason ?? error.data?.message ?? fallback;
  }
  return fallback;
}
</script>
<template>
  <fieldset :disabled="isBusy" class="tw-flex tw-flex-col tw-gap-3 tw-p-3 tw-text-gray-900">
    <MigrationExistingPlans v-model:selected-plan="viewedExistingPlan" />
    <template v-if="viewedExistingPlan == null">
      <fieldset class="tw-flex tw-items-center tw-gap-4 tw-text-sm">
        <legend class="tw-mb-1 tw-text-xs">Action</legend>
        <label class="tw-flex tw-cursor-pointer tw-items-center tw-gap-2">
          <input
            required
            type="radio"
            name="migration-action"
            :checked="action?.action === 'copy'"
            @change="() => (action = { action: 'copy', answers: [], state: { status: 'idle' } })"
          />
          Copy
        </label>
        <label class="tw-flex tw-cursor-pointer tw-items-center tw-gap-2">
          <input
            required
            type="radio"
            name="migration-action"
            :checked="action?.action === 'move'"
            @change="() => (action = { action: 'move', answers: [], state: { status: 'idle' } })"
          />
          Move
        </label>
      </fieldset>
      <div>
        <MigrationDestinationCase v-model.trim="destinationCaseReference" :source="sourceCaseReference" />
      </div>
      <div class="tw-flex tw-flex-col">
        <MigrationFileSelector v-model="selectedFileReferences" />
      </div>
      <p v-if="action?.state.status === 'error'" role="alert" class="tw-text-sm tw-text-red-600">
        {{ action?.state.message }}
      </p>
      <template v-if="action?.state.status === 'requiresInput'">
        <MigrationDecisions v-model="action.answers" :operations="action?.state.plan.operations" />
      </template>
      <template v-if="action?.state.status === 'ready'">
        <div
          role="status"
          class="tw-flex tw-flex-col tw-gap-1 tw-rounded-lg tw-border tw-border-gray-300 tw-bg-gray-100 tw-p-2 tw-text-sm"
        >
          <p>
            Ready to {{ action?.state.plan.action }} {{ action?.state.plan.operations.length }} file(s) to "{{
              action?.state.plan.destinationCaseReference
            }}".
          </p>
        </div>
      </template>
      <div class="tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">
        <button
          v-if="action == null || action.state.status === 'idle' || action.state.status === 'error'"
          type="button"
          class="tw-btn-md-gray tw-flex tw-items-center tw-gap-1"
          :disabled="!canCreatePlan"
          @click="createPlan"
        >
          Create Migration Plan
        </button>
        <button
          v-else-if="action.state.status === 'requiresInput'"
          type="button"
          class="tw-btn-md-gray tw-flex tw-items-center tw-gap-1"
          @click="createPlan"
        >
          Continue
        </button>
        <template v-else-if="action.state.status === 'ready' || action.state.status === 'migrating'">
          <button type="button" class="tw-btn-md-gray" @click="$emit('resetComponent')">Start Over</button>
          <button type="button" class="tw-btn-md-green tw-flex tw-items-center tw-gap-1" @click="confirmMigration">
            <i v-if="action?.state.status === 'migrating'" class="fas fa-spinner fa-sm fa-spin" aria-hidden="true" />
            Confirm Migration
          </button>
        </template>
      </div>
    </template>
  </fieldset>
</template>
